import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Modal, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { httpsCallable } from 'firebase/functions';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, functions } from '../../firebase';
import { useUsersData, Userprofile } from '../../Helpers/UsersContext';
import './ManageUsers.css';
import { Helmet } from 'react-helmet-async';


interface CreateUserRequest {
    name: string;
    email: string;
    password: string;
    admin?: boolean;
    accessToWeb?: boolean;
    groups?: string[];
}

interface UpdateUserRequest {
    uid: string;
    name?: string;
    admin?: boolean;
    accessToWeb?: boolean;
    groups?: string[];
}

const ManageUsers: React.FC = () => {
    const [user] = useAuthState(auth);
    const { users, loadingUsers, usersError, refreshUsers } = useUsersData();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState<Userprofile | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    
    // Modal-specific error states
    const [createModalError, setCreateModalError] = useState<string | null>(null);
    const [editModalError, setEditModalError] = useState<string | null>(null);

    // Form states
    const [newUser, setNewUser] = useState<CreateUserRequest>({
        name: '',
        email: '',
        password: '',
        admin: false,
        accessToWeb: false
    });

    const [editUser, setEditUser] = useState<UpdateUserRequest>({
        uid: '',
        name: '',
        admin: false,
        accessToWeb: false
    });

    // Firebase Functions
    const createUserFn = httpsCallable(functions, 'createUser');
    const updateAdminStatusFn = httpsCallable(functions, 'updateAdminStatus');
    const updateWebAccessFn = httpsCallable(functions, 'updateWebAccess');
    const updateUserNameFn = httpsCallable(functions, 'updateUserName');

    // Check if current user is admin
    useEffect(() => {
        if (user && users) {
            const currentUser = users.find(u => u.key === user.uid);
            setIsAdmin(currentUser?.admin || false);
        }
    }, [user, users]);

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setCreateModalError(null);

        // Client-side validation
        if (newUser.password.length < 6) {
            setCreateModalError('Lösenordet måste vara minst 6 tecken långt');
            setLoading(false);
            return;
        }

        try {
            await createUserFn(newUser);
            setNewUser({ name: '', email: '', password: '', admin: false, accessToWeb: false });
            await refreshUsers();
            // Close modal and show success message on parent container
            setShowCreateModal(false);
            setSuccess('Användare skapad framgångsrikt!');
        } catch (err: any) {
            setCreateModalError(err.message || 'Ett fel uppstod vid skapande av användare');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateAdminStatus = async (uid: string, admin: boolean) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await updateAdminStatusFn({ uid, admin });
            setSuccess(`Admin-status uppdaterad till ${admin ? 'aktiverad' : 'inaktiverad'}`);
            await refreshUsers();
        } catch (err: any) {
            setError(err.message || 'Ett fel uppstod vid uppdatering av admin-status');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateWebAccess = async (uid: string, accessToWeb: boolean) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await updateWebAccessFn({ uid, accessToWeb });
            setSuccess(`Webbåtkomst uppdaterad till ${accessToWeb ? 'aktiverad' : 'inaktiverad'}`);
            await refreshUsers();
        } catch (err: any) {
            setError(err.message || 'Ett fel uppstod vid uppdatering av webbåtkomst');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log('Updating user:', editUser);
        console.log('Original user:', editingUser);
        setLoading(true);
        setEditModalError(null);

        try {
            // Update name if it changed
            if (editUser.name && editUser.name !== editingUser?.name) {
                console.log('Updating name:', editUser.name);
                await updateUserNameFn({ uid: editUser.uid, name: editUser.name });
            }

            // Update admin status if it changed
            if (editUser.admin !== editingUser?.admin) {
                console.log('Updating admin status:', editUser.admin);
                await updateAdminStatusFn({ uid: editUser.uid, admin: editUser.admin });
            }

            // Update web access if it changed
            if (editUser.accessToWeb !== editingUser?.accessToWeb) {
                console.log('Updating web access:', editUser.accessToWeb);
                await updateWebAccessFn({ uid: editUser.uid, accessToWeb: editUser.accessToWeb });
            }

            await refreshUsers();
            // Close modal and show success message on parent container
            setShowEditModal(false);
            setEditingUser(null);
            setSuccess('Användare uppdaterad framgångsrikt!');
        } catch (err: any) {
            console.error('Error updating user:', err);
            setEditModalError(err.message || 'Ett fel uppstod vid uppdatering av användare');
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (userData: Userprofile) => {
        setEditingUser(userData);
        setEditUser({
            uid: userData.key,
            name: userData.name,
            admin: userData.admin || false,
            accessToWeb: userData.accessToWeb || false
        });
        setEditModalError(null);
        setShowEditModal(true);
    };

    const openCreateModal = () => {
        setNewUser({ name: '', email: '', password: '', admin: false, accessToWeb: false });
        setCreateModalError(null);
        setShowCreateModal(true);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Aldrig';
        try {
            return new Date(dateString).toLocaleDateString('sv-SE');
        } catch {
            return 'Ogiltigt datum';
        }
    };

    if (!isAdmin) {
        return (
            <Container>
                <Alert variant="warning">
                    <Alert.Heading>Åtkomst nekad</Alert.Heading>
                    <p>Du har inte behörighet att hantera användare. Endast administratörer kan komma åt denna sida.</p>
                </Alert>
            </Container>
        );
    }

    if (loadingUsers) {
        return (
            <Container className="text-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Laddar användare...</span>
                </Spinner>
                <p className="mt-3">Laddar användare...</p>
            </Container>
        );
    }

    if (usersError) {
        return (
            <Container>
                <Alert variant="danger">
                    <Alert.Heading>Fel vid laddning av användare</Alert.Heading>
                    <p>{usersError.message}</p>
                    <Button variant="outline-danger" onClick={refreshUsers}>
                        Försök igen
                    </Button>
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>Hantera användare - Molnfastigheter</title>
            </Helmet>
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="mb-1">Hantera användare</h2>
                            <p className="text-muted mb-0">Skapa och hantera användarkonton och behörigheter</p>
                        </div>
                        <Button 
                            variant="primary" 
                            onClick={openCreateModal}
                            disabled={loading}
                        >
                            Skapa ny användare
                        </Button>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3">
                    <Col>
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {success && (
                <Row className="mb-3">
                    <Col>
                        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Row>
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Användarlista ({users?.length || 0} användare)</h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive hover className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Namn</th>
                                        <th>Admin</th>
                                        <th>Webbåtkomst</th>
                                        <th>Skapad av</th>
                                        <th>Senast aktiv</th>
                                        <th>Enhet</th>
                                        <th>Åtgärder</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users?.map((userData) => (
                                        <tr key={userData.key}>
                                            <td>
                                                <div>
                                                    <strong>{userData.name}</strong>
                                                    <br />
                                                    <small className="text-muted">{userData.key}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <Badge 
                                                    bg={userData.admin ? 'success' : 'secondary'}
                                                    className="cursor-pointer user-badge"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleUpdateAdminStatus(userData.key, !userData.admin);
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {userData.admin ? 'Ja' : 'Nej'}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge 
                                                    bg={userData.accessToWeb ? 'success' : 'secondary'}
                                                    className="cursor-pointer user-badge"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleUpdateWebAccess(userData.key, !userData.accessToWeb);
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {userData.accessToWeb ? 'Ja' : 'Nej'}
                                                </Badge>
                                            </td>
                                            <td>
                                                {userData.createdBy ? (
                                                    <div>
                                                        <small className="text-muted">{userData.createdBy}</small>
                                                        <br />
                                                        <small className="text-muted">{formatDate(userData.createdAt)}</small>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td>{formatDate(userData.lastAppStart)}</td>
                                            <td>
                                                {userData.lastDeviceUsed ? (
                                                    <div>
                                                        <small>{userData.lastDeviceUsed.model}</small>
                                                        <br />
                                                        <small className="text-muted">{userData.lastDeviceUsed.systemName} {userData.lastDeviceUsed.systemVersion}</small>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td>
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    onClick={() => openEditModal(userData)}
                                                    disabled={loading}
                                                >
                                                    Redigera
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Create User Modal */}
            <Modal show={showCreateModal} onHide={() => {
                setShowCreateModal(false);
                setCreateModalError(null);
            }} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Skapa ny användare</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCreateUser}>
                    <Modal.Body>
                        {createModalError && (
                            <Alert variant="danger" dismissible onClose={() => setCreateModalError(null)}>
                                {createModalError}
                            </Alert>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>E-postadress</Form.Label>
                            <Form.Control
                                type="email"
                                value={newUser.email}
                                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                placeholder="Ange användarens e-postadress"
                                required
                            />
                            <Form.Text className="text-muted">
                                E-postadress som används för inloggning
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Namn</Form.Label>
                            <Form.Control
                                type="text"
                                value={newUser.name}
                                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                                placeholder="Ange användarens namn"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Lösenord</Form.Label>
                            <Form.Control
                                type="password"
                                value={newUser.password}
                                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                placeholder="Ange lösenord för användaren"
                                required
                                minLength={6}
                            />
                            <Form.Text className="text-muted">
                                Minst 6 tecken. Användaren kan ändra lösenordet efter första inloggningen.
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Administratör"
                                checked={newUser.admin}
                                onChange={(e) => setNewUser({ ...newUser, admin: e.target.checked })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Webbåtkomst"
                                checked={newUser.accessToWeb}
                                onChange={(e) => setNewUser({ ...newUser, accessToWeb: e.target.checked })}
                            />
                        </Form.Group>
                        <Alert variant="info" className="mt-3">
                            <small>
                                <strong>Obs:</strong> En Firebase UID genereras automatiskt för den nya användaren. 
                                Användaren kommer att kunna logga in med den angivna e-postadressen och lösenordet.
                                Användaren kan ändra lösenordet efter första inloggningen.
                            </small>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => {
                            setShowCreateModal(false);
                            setCreateModalError(null);
                        }}>
                            Avbryt
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Skapar...' : 'Skapa användare'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit User Modal */}
            <Modal show={showEditModal} onHide={() => {
                setShowEditModal(false);
                setEditingUser(null);
                setEditModalError(null);
            }} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Redigera användare</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateUser}>
                    <Modal.Body>
                        {editModalError && (
                            <Alert variant="danger" dismissible onClose={() => setEditModalError(null)}>
                                {editModalError}
                            </Alert>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Namn</Form.Label>
                            <Form.Control
                                type="text"
                                value={editUser.name}
                                onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                                placeholder="Ange användarens namn"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Administratör"
                                checked={editUser.admin}
                                onChange={(e) => setEditUser({ ...editUser, admin: e.target.checked })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Webbåtkomst"
                                checked={editUser.accessToWeb}
                                onChange={(e) => setEditUser({ ...editUser, accessToWeb: e.target.checked })}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => {
                            setShowEditModal(false);
                            setEditingUser(null);
                            setEditModalError(null);
                        }}>
                            Avbryt
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Uppdaterar...' : 'Uppdatera användare'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default ManageUsers;
